// Copyright 2024, Durable
/*
 * An array of lazy imports for pages that should only be accessed by elevated
 * users, such as support and developers.
 */
import React from "react";

// Support Pages use outlined icons
import {
  AppRegistration as AppRegistrationIcon,
  DeveloperBoardOutlined as DeveloperBoardIcon,
  SpeedOutlined as SpeedIcon,
  MapOutlined as MapIcon,
  CoffeeMakerOutlined as CoffeeMakerIcon,
  PeopleOutline as PeopleIcon,
  DataObject as DataObjectIcon
} from "@mui/icons-material";
import { route } from "./userPages";

const AppDashboard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "appdash" */
      /* webpackMode: "lazy" */
      "../pages/AppDashboard"
    )
);
const DeveloperDashboard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "DevDash" */
      /* webpackMode: "lazy" */
      "../pages/DeveloperDashboard"
    )
);
const DebugPlanner = React.lazy(
  () =>
    import(
      /* webpackChunkName: "DebugPlanner" */
      /* webpackMode: "lazy" */
      "../pages/DebugPlanner"
    )
);
const PerformanceVis = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PerformanceVis" */
      /* webpackMode: "lazy" */
      "../pages/PerformanceVis"
    )
);
const PlanDashboard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PlanDashboard" */
      /* webpackMode: "lazy" */
      "../pages/PlanDashboard"
    )
);
const DeployConfigViewer = React.lazy(
  () =>
    import(
      /* webpackChunkName: "DataGenTool" */
      /* webpackMode: "lazy" */
      "../pages/deploymentConfig"
    )
);
const UserManager = React.lazy(
  () =>
    import(
      /* webpackChunkName: "DataGenTool" */
      /* webpackMode: "lazy" */
      "../pages/userManagement"
    )
);
// An array containing all current debug and development tools.
// Populating this array will automatically route your debug tool and populate
// all developer areas.
const supportPages: Array<route> = [
  {
    id: "app_dashboard",
    name: "App Dashboard",
    route: "/app/appdash",
    element() {
      return <AppDashboard />;
    },
    desc: "Controls for an app you have previously created.",
    icon() {
      return <AppRegistrationIcon fontSize="inherit" />;
    },
    routeOnly: true
  },
  {
    id: "dev_dash",
    name: "Developer Dashboard",
    route: "/app/util/dashboard",
    element() {
      return <DeveloperDashboard />;
    },
    desc: "Access all the resources needed to develop durable.",
    icon() {
      return <DataObjectIcon fontSize="inherit" />;
    }
  },
  {
    id: "plan_dash",
    name: "Plan Dashboard",
    route: "/app/util/plandash",
    element() {
      return <PlanDashboard />;
    },
    desc: "View historical and realtime plan data.",
    icon() {
      return <MapIcon fontSize="inherit" />;
    }
  },
  {
    id: "debug_planner",
    name: "Debug Planner",
    route: "/app/util/planner",
    element() {
      return <DebugPlanner />;
    },
    desc: "Visualize the planning for the inference path.",
    icon() {
      return <DeveloperBoardIcon fontSize="inherit" />;
    },
    dontRoute: true
  },
  {
    id: "perf_log",
    name: "Performance Log",
    route: "/app/util/perflog",
    element() {
      return <PerformanceVis />;
    },
    desc: "Performance log output visualizer.",
    icon() {
      return <SpeedIcon fontSize="inherit" />;
    }
  },
  {
    id: "deploy_config",
    name: "Deploy Config Viewer",
    route: "/app/util/deploy-config-viewer",
    element() {
      return <DeployConfigViewer />;
    },
    desc: "View the configuration of your current deployment.",
    icon() {
      return <CoffeeMakerIcon fontSize="inherit" />;
    }
  },
  {
    id: "user_management",
    name: "User Management",
    route: "/app/util/user-management",
    element() {
      return <UserManager />;
    },
    desc: "Manage all the users.",
    icon() {
      return <PeopleIcon fontSize="inherit" />;
    }
  }
];

export default supportPages;
